import React, { useState, useEffect } from "react";
import Button from "./Button";
import { DateTime } from "luxon";
import { useStateContext } from "../contexts/ContextProvider";
import { FaRegFilePdf } from "react-icons/fa";
import ProgressBar from "./ProgressBar";
import BillCheckboxes from "./BillCheckboxes";
import { apiCreateLease, apiUpdateLease } from "../services/apiService";
import { storage } from "../services/firebaseConfig";
import { removeOldFile } from "../services/firebaseService";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getRandomString } from "../helpers/randomString";
import AddressForm from "./AddressForm";
import { lawsByState } from "../helpers/leaseDataAndFunctions";
import { getOrdinalSuffix } from "../helpers/helperFunctions";

const initialErrorState = {
  firstName: "",
  lastName: "",
  email: "",
  payPeriod: "",
  rent: "",
  splitsBills: "",
  otherType: "",
  leaseTerm: "",
  leaseStart: "",
  terms: "",
  noFileError: "",
  fileTypeError: "",
  fileSizeError: "",
};

const AddRoommate = (props) => {
  const { room, propertyId, lease /*setStripeOnboarding*/ } = props;
  const {
    user,
    // stripeConnectAccount,
    leases,
    usersProperties,
    setLeases,
    handleModal,
    address,
    setAddress,
    resetAddress,
    hasErrors,
  } = useStateContext();

  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileTypeError, setFileTypeError] = useState(false);
  const [changeEmailDisabled, setChangeEmailDisabled] = useState(false);
  const [disabledEmailStyle, setDisabledEmailStyle] = useState("");
  const [error, setError] = useState(initialErrorState);
  const [terms, setTerms] = useState(false);
  const [addMailingAddress, setAddMailingAddress] = useState(false);
  const [addendumFileName, setAddendumFileName] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [addendum, setAddendum] = useState({
    rulesAddendum: false,
    disclosuresAddendum: false,
    otherAddendum: false,
  });

  const [prorate, setProrate] = useState({ month: "", amount: 0 });
  const [page, setPage] = useState(1);

  const pages = 14;
  const maxFileSizeMB = 5;

  const currentProperty = usersProperties.find((x) => x.id === propertyId);
  const currentLaws = lawsByState.find(
    (x) => x.state === currentProperty.state
  );

  const [roommate, setRoommate] = useState({
    landlordId: user.id,
    landlordUuid: user.landlordUuid,
    propertyId,
    roomId: room.id,
    status: "created", // created, pending, active
    tenantFirstName: "",
    tenantMiddleName: "",
    tenantLastName: "",
    tenantEmail: "",
    tenantPhone: "",
    roomName: room.roomName,
    rent: room.rent,
    securityDep: room.securityDep,
    payPeriod: "--",
    gracePeriod: currentLaws.gracePeriod,
    leaseTerm: "--",
    leaseStart: "",
    leaseEnd: "",
    leasePdf: "",
    splitsBills: false,
    isFurnished: room.isFurnished,
    petsAllowed: room.catsOk || room.dogsOk,
    petDeposit: "",
    prorateAmount: null,
    paysLateFee: false,
    rulesAddendum: "",
    disclosuresAddendum: "",
    otherAddendum: "",
    hasMailingAddress: addMailingAddress,
    mailingStreet: "",
    mailingStreet2: "",
    mailingCity: "",
    mailingState: "",
    mailingZip: "",
    // allowElectronicPayments: stripeSubscription.type === "basic" ? false : true,
    landlordFont: "",
    tenantFont: "",
    landlordDateSigned: "",
    tenantDateSigned: "",
    paysElectric: false,
    paysGas: false,
    paysWater: false,
    paysSewer: false,
    paysCable: false,
    paysPhone: false,
    paysInternet: false,
    paysHoa: false,
    // paysOther: false,
    // otherType: "",
  });

  useEffect(() => {
    if (lease) {
      // if (stripeSubscription.type !== "basic")
      //   lease.allowElectronicPayments = true; // encourage e-pay
      setRoommate(lease);
      setChangeEmailDisabled(true);
      setDisabledEmailStyle("bg-gray-300");
      setAddMailingAddress(lease.hasMailingAddress);
      setAddress((prevState) => ({
        ...prevState,
        street: lease.mailingStreet,
        street2: lease.mailingStreet2,
        city: lease.mailingCity,
        state: lease.mailingState,
        zipCode: lease.mailingZip,
      }));
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    resetProrate();
    calculateProrate();
    setLeaseEndDate();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roommate.leaseStart, roommate.leaseTerm, roommate.payPeriod]);

  const nextPage = () => {
    if (
      hasLocalErrors() ||
      (page === 13 && addMailingAddress ? hasErrors(true) : false)
    ) {
      console.log("next page error!!!");
      return;
    } else {
      if (page === 13) handleMailingAddress();
      resetAddendumDropboxes();
      setPage(page + 1);
    }
  };

  const prevPage = () => {
    resetAddendumDropboxes();
    setPage(page - 1);

    // reset addendum errors
    setFileSizeError(false);
    setFileTypeError(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const reg = new RegExp("^[0-9]*$"); // integers or empty?
    let safeValue;

    if (name === "splitsBills") handleBillReset(value);

    // ensure numbers only
    if (name === "rent" || name === "securityDep" || name === "petDeposit") {
      if (reg.test(value)) {
        safeValue = value;
      } else {
        console.log(name, "bad value");
        safeValue = "";
        return;
      }
      if (name === "rent") {
        //TODO: investigate this: improper state managment?
        room.rent = safeValue;
      }
      if (name === "securityDep") {
        //TODO: investigate this: imporper statemanagement?
        room.securityDep = safeValue;
      }
    } else {
      safeValue = value;
    }

    setRoommate((prevState) => ({ ...prevState, [name]: safeValue }));
  };

  const handleCheckbox = (event) => {
    const { name, checked } = event.target;
    setRoommate((prevState) => ({ ...prevState, [name]: checked }));
  };

  const handleBillReset = (value) => {
    if (roommate.splitsBills !== value) {
      setRoommate((prevState) => ({
        ...prevState,
        paysCable: false,
        paysElectric: false,
        paysGas: false,
        paysHoa: false,
        paysInternet: false,
        paysPhone: false,
        paysSewer: false,
        paysWater: false,
        paysOther: false,
      }));
    }
  };

  const handleAddendums = (event) => {
    const { name, value } = event.target;
    setAddendum((prevState) => ({ ...prevState, [name]: value }));
    if (value === "false") {
      if (roommate[name]) removeOldFile(roommate[name]);
      setRoommate((prevState) => ({ ...prevState, [name]: "" }));
      setFileSizeError(false);
      setFileTypeError(false);
    }
  };

  const resetAddendumDropboxes = () => {
    if (page === 11 && roommate.rulesAddendum === "") {
      setAddendum((prevState) => ({
        ...prevState,
        rulesAddendum: false,
      }));
    }
    // if (page === 12 && roommate.disclosuresAddendum === "") {
    //   setAddendum((prevState) => ({
    //     ...prevState,
    //     disclosuresAddendum: false,
    //   }));
    // }
    if (page === 12 && roommate.otherAddendum === "") {
      setAddendum((prevState) => ({
        ...prevState,
        otherAddendum: false,
      }));
    }
  };

  const calculateProrate = () => {
    if (roommate.leaseStart !== "") {
      let prorateAmount = null;
      if (roommate.payPeriod === "weekly") {
        const weekDay = DateTime.fromISO(roommate.leaseStart).weekday;
        if (weekDay === 1) {
          console.log("no weekday prorate needed!");
          setProrate({ month: "", amount: 0 });
          return;
        } else {
          const prorateDays = 7 - (weekDay - 1);
          prorateAmount = ((roommate.rent / 7) * prorateDays).toFixed(2);
          setProrate({ month: "", amount: prorateAmount });
          console.log("weekly Pro-rate: ", prorateAmount);
        }
      } else {
        const startDay = DateTime.fromISO(roommate.leaseStart).day;
        if (startDay === 1) {
          // console.log("no prorate needed!");
          setProrate({ month: "", amount: 0 });
          return;
        } else {
          const month = DateTime.fromISO(roommate.leaseStart).monthLong;
          const daysInMonth = DateTime.fromISO(roommate.leaseStart).daysInMonth;
          const prorateDays = daysInMonth - (startDay - 1);
          prorateAmount = ((roommate.rent / daysInMonth) * prorateDays).toFixed(
            2
          );
          setProrate({ month: month, amount: prorateAmount });
          // console.log("Monthly Pro-rate: ", prorateAmount);
        }
      }
      setRoommate((prevState) => ({
        ...prevState,
        prorateAmount: prorateAmount,
      }));
    }
  };

  const resetProrate = () => {
    setProrate({ month: "", amount: 0 });
    setRoommate((prevState) => ({
      ...prevState,
      prorateAmount: null,
    }));
  };

  const setLeaseEndDate = () => {
    if (roommate.leaseTerm === "12 months") {
      const endDate = DateTime.fromISO(roommate.leaseStart)
        .plus({ years: 1 })
        .minus({ month: 1 })
        .endOf("month")
        .toFormat("yyyy-MM-dd");

      // console.log("Lease Ends: ", endDate);
      setRoommate((prevState) => ({
        ...prevState,
        leaseEnd: endDate,
      }));
    }

    if (roommate.leaseTerm === "6 months") {
      const endDate = DateTime.fromISO(roommate.leaseStart)
        .plus({ month: 5 })
        .endOf("month")
        .toFormat("yyyy-MM-dd");

      // console.log("Lease Ends: ", endDate);
      setRoommate((prevState) => ({
        ...prevState,
        leaseEnd: endDate,
      }));
    }

    if (roommate.leaseTerm === "3 months") {
      const endDate = DateTime.fromISO(roommate.leaseStart)
        .plus({ month: 2 })
        .endOf("month")
        .toFormat("yyyy-MM-dd");

      // console.log("Lease Ends: ", endDate);
      setRoommate((prevState) => ({
        ...prevState,
        leaseEnd: endDate,
      }));
    }

    if (roommate.leaseTerm === "1 month") {
      const endDate = DateTime.fromISO(roommate.leaseStart)
        .plus({ month: 0 })
        .endOf("month")
        .toFormat("yyyy-MM-dd");

      // console.log("Lease Ends: ", endDate);
      setRoommate((prevState) => ({
        ...prevState,
        leaseEnd: endDate,
      }));
    }
  };

  const handleAddRoommate = async () => {
    setIsLoading(true);
    if (hasLocalErrors()) {
      console.log("local error!!");
      setIsLoading(false);
      return;
    } else {
      const response = await apiCreateLease(roommate);
      if (response.hasError) {
        console.log(response.error.response.statusText);
        setIsLoading(false);
        return;
      } else {
        setLeases((prevState) => [...prevState, response.data]);
        handleModal("addRoommate");
        setIsLoading(false);
        // if (
        //   /*response.data.allowElectronicPayments &&*/ !stripeConnectAccount
        // ) {
        //   setStripeOnboarding(true);
        // }
      }
    }
  };

  const handleLeaseUpdate = async () => {
    if (hasLocalErrors()) return;
    const response = await apiUpdateLease(roommate.id, roommate);
    if (response.hasError) {
      //TODO: add error message
      console.log(response.error);
      return;
    } else {
      //TODO: this is nonsense, clean it up
      let leasesCopy = leases;
      let newLeases = leasesCopy.toSpliced(
        leasesCopy.findIndex((x) => x.id === roommate.id),
        1,
        response.data
      );
      setLeases(newLeases);
      handleModal("addRoommate");
      //   if (/*response.data.allowElectronicPayments &&*/ !stripeConnectAccount)
      //     setStripeOnboarding(true);
    }
  };

  const handleUpload = async (event) => {
    const { name, files } = event.target;
    const file = files[0];

    setFileSizeError(false);
    setFileTypeError(false);

    if (file.size > maxFileSizeMB * 1024 * 1024) {
      setFileSizeError(true);
      document.getElementById("add-addendum").value = "";
      return;
    } else if (file.type === "application/pdf") {
      // define storge location, and new file name
      const imageRef = ref(
        storage,
        `leases/${user.uuid}/${getRandomString(20)}.${file.name.split(".")[1]}`
      );
      // save file name
      setAddendumFileName((prevState) => ({ ...prevState, [name]: file.name }));

      // upload file to cloud
      await uploadBytes(imageRef, file).then(async (snapshot) => {
        await getDownloadURL(snapshot.ref).then((url) => {
          // collect url, and add to array
          console.log("uploaded file");
          const oldFile = roommate[name];
          setRoommate((prevState) => ({ ...prevState, [name]: url }));
          if (oldFile) removeOldFile(oldFile);
        });
      });
      return;
    }
    setFileTypeError(true);
    document.getElementById("add-addendum").value = "";
  };

  // const removeOldFile = (url) => {
  //   console.log(url);
  //   // delete image from image server
  //   const imageRef = ref(storage, url);
  //   getDownloadURL(imageRef)
  //     .then(() => {
  //       // File exists, proceed with deletion
  //       deleteObject(imageRef)
  //         .then(() => {
  //           console.log("File deleted successfully!");
  //         })
  //         .catch((error) => {
  //           console.error("Error deleting file:", error);
  //         });
  //     })
  //     .catch((error) => {
  //       // File does not exist or other error occurred
  //       if (error.code === "storage/object-not-found") {
  //         console.log("File does not exist, no need to delete.");
  //       } else {
  //         console.error("Error checking file existence:", error);
  //       }
  //     });
  // };

  const handleMailingAddress = () => {
    setRoommate((prevState) => ({
      ...prevState,
      hasMailingAddress: addMailingAddress,
      mailingStreet: address.street,
      mailingStreet2: address.street2,
      mailingCity: address.city,
      mailingState: address.state,
      mailingZip: address.zipCode,
    }));
  };

  const hasLocalErrors = () => {
    const reg = new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/); // valid email format?
    if (page === 1 && roommate.tenantFirstName === "") {
      activateError("firstName");
    } else if (page === 3 && roommate.tenantLastName === "") {
      activateError("lastName");
    } else if (
      page === 4 &&
      (roommate.tenantEmail === "" ||
        !reg.test(roommate.tenantEmail) ||
        user.email === roommate.tenantEmail)
    ) {
      activateError("email");
    } else if (page === 5 && roommate.payPeriod === "--") {
      activateError("payPeriod");
    } else if (page === 5 && roommate.rent < 1) {
      activateError("rent");
    } else if (page === 6 && roommate.leaseTerm === "--") {
      activateError("leaseTerm");
    } else if (
      page === 7 &&
      roommate.splitsBills === "true" &&
      !roommate.paysElectric &&
      !roommate.paysGas &&
      !roommate.paysWater &&
      !roommate.paysSewer &&
      !roommate.paysCable &&
      !roommate.paysPhone &&
      !roommate.paysInternet &&
      !roommate.paysHoa &&
      !roommate.paysOther
    ) {
      activateError("splitsBills");
    } else if (
      page === 7 &&
      roommate.splitsBills &&
      roommate.paysOther &&
      roommate.otherType === ""
    ) {
      activateError("otherType");
    } else if (page === 8 && roommate.leaseStart === "") {
      activateError("leaseStart");
    } else if (page === pages && terms === false) {
      activateError("terms");
    } else if (page >= 11 && page <= 12 && fileTypeError) {
      activateError("fileTypeError");
    } else if (page >= 11 && page <= 12 && fileSizeError) {
      activateError("fileSizeError");
    } else {
      return false;
    }
    return true;
  };

  const activateError = (item) => {
    setError((prevState) => ({
      ...prevState,
      [item]: "animate-shake text-red-600 border-red-600",
    }));
    setTimeout(() => {
      setError((prevState) => ({
        ...prevState,
        [item]: "",
      }));
    }, 1000);
    clearTimeout();
  };

  return (
    <div
      className="w-screen h-screen fixed top-0 left-0 right-0 bg-white mt-20 z-30 
    overflow-y-auto overscroll-none"
    >
      <ProgressBar pages={pages} currentPage={page} />
      <form
        className="grid justify-items-center"
        onSubmit={(e) => {
          e.preventDefault();
          page === pages ? handleAddRoommate() : nextPage();
          // console.log("enter key submit");
        }}
      >
        {page === 1 && (
          <>
            <div className={`flex justify-center mt-10 ${error.firstName}`}>
              What is your roommate's first name?
              <span className="text-red-500">*</span>
            </div>
            <label className="grid mt-5">
              <input
                id="tenant-first-name"
                name="tenantFirstName"
                type="text"
                placeholder="First Name"
                value={roommate.tenantFirstName}
                autoFocus={true}
                autoComplete="given-name"
                className={`border-gray-300 border-1 rounded-md pl-1 ${error.firstName}`}
                onChange={handleChange}
              />
            </label>
          </>
        )}
        {page === 2 && (
          <>
            <div className="flex justify-center mt-10">
              What is your roommate's middle name?
            </div>
            <label className="grid mt-5">
              <input
                id="tenant-middle-name"
                name="tenantMiddleName"
                type="text"
                placeholder="Middle Name"
                autoFocus={true}
                autoComplete="additional-name"
                value={roommate.tenantMiddleName || ""}
                className="border-gray-300 border-1 rounded-md pl-1"
                onChange={handleChange}
              />
              {/* <div>(Skip if none)</div> */}
            </label>
          </>
        )}
        {page === 3 && (
          <>
            <div className={`flex justify-center mt-10 ${error.lastName}`}>
              What is your roommate's last name?
              <span className="text-red-500">*</span>
            </div>
            <label className="grid mt-5">
              <input
                id="tenant-last-name"
                name="tenantLastName"
                type="text"
                placeholder="Last Name"
                value={roommate.tenantLastName}
                autoFocus={true}
                autoComplete="family-name"
                className={`border-gray-300 border-1 rounded-md pl-1 ${error.lastName}`}
                onChange={handleChange}
              />
            </label>
          </>
        )}
        {page === 4 && (
          <>
            <div className={`flex justify-center mt-10 ${error.email}`}>
              What is your roommate's email?
              <span className="text-red-500">*</span>
            </div>
            <label className="grid mt-5">
              <input
                id="tenant-email"
                name="tenantEmail"
                type="email"
                placeholder="Email"
                value={roommate.tenantEmail}
                autoFocus={true}
                disabled={changeEmailDisabled}
                autoComplete="email"
                className={`border-gray-300 border-1 rounded-md pl-1 ${error.email} ${disabledEmailStyle}`}
                onChange={handleChange}
              />
            </label>
            {changeEmailDisabled && (
              <div className="p-10">
                <div
                  className="text-center bg-gray-100 border-gray-400 
                border-2 rounded-2xl mt-5 p-5"
                >
                  If you need to change your tenant's email, you must delete
                  this invite and start again.
                </div>
              </div>
            )}
          </>
        )}
        {page === 5 && (
          <>
            <div
              className={`text-justified mt-10 mb-5 px-10 ${error.payPeriod}`}
            >
              How often is the rent due?
              <span className="text-red-500">*</span>
            </div>
            <label className="grid">
              <select
                id="pay-period"
                name="payPeriod"
                value={roommate.payPeriod}
                className={`w-40 border-black border-1 ${error.payPeriod}`}
                autoFocus={true}
                onChange={handleChange}
                onChangeCapture={() => {}}
              >
                <option disabled>--</option>
                <option value="monthly">Monthly</option>
                {/* <option value="weekly">Weekly</option> */}
              </select>
            </label>
            {roommate.payPeriod !== "--" && (
              <>
                <div
                  className={`text-lg text-justified mt-10 mb-1 px-10 ${error.rent}`}
                >
                  How much is the {roommate.payPeriod} rent?
                  <span className="text-red-500">*</span>
                </div>
                <label className="grid">
                  <div className="flex relative">
                    <div className="absolute left-2">$</div>
                    <input
                      id="rent"
                      name="rent"
                      type="tel"
                      maxLength={5}
                      placeholder={800}
                      value={roommate.rent}
                      className={`w-40 text-right border-black border-1 pr-8 ${error.rent}`}
                      onChange={handleChange}
                    />
                    <div className="absolute right-2 top-.05">.00</div>
                  </div>
                </label>
                <div className="text-lg text-justified mt-10 px-10">
                  Is there a security deposit?
                </div>
                <label className="grid">
                  <div className="flex relative">
                    <div className="absolute left-2">$</div>
                    <input
                      id="security-dep"
                      name="securityDep"
                      type="tel"
                      maxLength={5}
                      placeholder={0}
                      value={roommate.securityDep}
                      className="w-40 text-right border-black border-1 pr-8"
                      onChange={handleChange}
                    />
                    <div className="absolute right-2 top-.05">.00</div>
                  </div>
                </label>
              </>
            )}
          </>
        )}
        {page === 6 && (
          <>
            <div className={`flex justify-center mt-10 ${error.leaseTerm}`}>
              What is the term of this lease?
              <span className="text-red-500">*</span>
            </div>
            <label className="grid mt-5">
              <select
                id="lease-term"
                name="leaseTerm"
                value={roommate.leaseTerm}
                autoFocus={true}
                className={`border-gray-300 border-1 rounded-md pl-1 ${error.leaseTerm}`}
                onChange={handleChange}
              >
                <option disabled>--</option>
                <option value="12 months">1 Year</option>
                <option value="6 months">6 Months</option>
                <option value="3 months">3 Months</option>
                <option value="1 month">Month-to-month</option>
              </select>
            </label>
            {roommate.leaseTerm !== "--" && (
              <div className="max-w-[500px] p-10">
                <div
                  className=" text-center bg-gray-100 border-gray-400 
                border-2 rounded-2xl mt-5 p-5"
                >
                  After {roommate.leaseTerm} the lease will default to a
                  month-to-month basis.
                </div>
              </div>
            )}
          </>
        )}
        {page === 7 && (
          <>
            <div
              className={`text-lg text-center text-justified mt-5 mb-5 px-10`}
            >
              Will this roommate contribute to house bills? (E.g. Utilities,
              Wifi, etc.)
            </div>
            <label className="grid">
              <select
                id="splits-bills"
                name="splitsBills"
                value={roommate.splitsBills}
                className={`w-[185px] border-black border-1 ${error.splitsBills}`}
                autoFocus={true}
                onChange={
                  handleChange
                  // handleBillReset();
                }
              >
                {/* <option disabled>--</option> */}
                <option value={false}>No</option>
                <option value={true}>Yes</option>
              </select>
            </label>
            {roommate.splitsBills === "true" && (
              <BillCheckboxes
                handleChange={handleChange}
                handleCheckbox={handleCheckbox}
                roommate={roommate}
                error={error}
              />
            )}
          </>
        )}

        {page === 8 && (
          <>
            <div className={`flex justify-center mt-10 ${error.leaseStart}`}>
              When do you want this lease to begin?
              <span className="text-red-500">*</span>
            </div>
            <label className="grid mt-5">
              <input
                id="lease-start"
                name="leaseStart"
                type="date"
                min={DateTime.now().toFormat("yyyy-MM-dd")}
                value={roommate.leaseStart}
                autoFocus={true}
                className={`border-gray-300 border-1 rounded-md pl-1${error.leaseStart}`}
                onChange={handleChange}
              />
            </label>
            {prorate.amount !== 0 && roommate.leaseStart !== "" && (
              <div className="p-10">
                <div
                  className="text-center bg-gray-100 border-gray-400 
                border-2 rounded-2xl mt-5 p-5"
                >
                  This date creates a pro-rate of{" "}
                  {Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    maximumFractionDigits: 2,
                  }).format(prorate.amount)}{" "}
                  for{" "}
                  {prorate.month === ""
                    ? "the first week"
                    : `the rest of ${prorate.month}`}
                  .
                </div>
              </div>
            )}
          </>
        )}
        {page === 9 && (
          <>
            <div className="flex justify-center mt-10">
              Do you want to charge late fees?
              <span className="text-red-500">*</span>
            </div>
            <label className="grid mt-5">
              <select
                id="pays-late-fee"
                name="paysLateFee"
                value={roommate.paysLateFee}
                className="w-40 border-gray-300 border-1 rounded-md"
                autoFocus={true}
                onChange={handleChange}
                onChangeCapture={() => console.log("change complete")}
              >
                <option value={false}>No</option>
                <option value={true}>Yes</option>
              </select>
            </label>
            {roommate.paysLateFee && (
              <div className="max-w-[500px] p-10">
                <div
                  className="text-center bg-gray-100 border-gray-400 
                border-2 rounded-2xl mt-5 p-5"
                >
                  On the {roommate.gracePeriod ? roommate.gracePeriod + 1 : "6"}
                  <sup>{getOrdinalSuffix(roommate.gracePeriod)}</sup> of each
                  month, the tenant will be charged the lesser of $50 or 5%, on
                  any unpaid balance.
                </div>
              </div>
            )}
          </>
        )}
        {page === 10 && (
          <>
            <div className="flex justify-center mt-10">Are pets allowed?</div>
            <label className="grid mt-5">
              <select
                id="pets-allowed"
                name="petsAllowed"
                value={roommate.petsAllowed}
                className="w-40 border-gray-300 border-1 rounded-md"
                autoFocus={true}
                onChange={() =>
                  setRoommate((prevState) => ({
                    ...prevState,
                    petsAllowed: !roommate.petsAllowed,
                    petDeposit: "",
                  }))
                }
                onChangeCapture={() => {}}
              >
                <option value={false}>No</option>
                <option value={true}>Yes</option>
              </select>
            </label>
            {roommate.petsAllowed && (
              <div className="p-10">
                <div className="flex justify-center">Pet deposit amount?</div>
                <label className="grid mt-5">
                  <div className="flex relative">
                    <div className="absolute left-2">$</div>
                    <input
                      id="pet-deposit"
                      name="petDeposit"
                      type="tel"
                      maxLength={5}
                      placeholder={0}
                      value={roommate.petDeposit}
                      className="w-40 text-right border-gray-300 border-1 rounded-md pr-8"
                      onChange={handleChange}
                    />
                    <div className="absolute right-2 top-0.5">.00</div>
                  </div>
                </label>
              </div>
            )}
          </>
        )}
        {page === 11 && (
          <>
            <div className="flex justify-center mt-10 pl-16 pr-10">
              Would you like to add "House Rules" as an addendum to the lease?
              (Optional)
            </div>
            <label className="grid mt-5">
              <select
                id="add-rules-addendum"
                name="rulesAddendum"
                value={addendum.rulesAddendum}
                className="w-40 border-gray-300 border-1 rounded-md"
                autoFocus={true}
                onChange={handleAddendums}
              >
                <option value={false}>No</option>
                <option value={true}>Yes</option>
              </select>
            </label>
            {addendum.rulesAddendum === "true" && (
              <div>
                <div className="grid justify-items-center h-20 mt-10">
                  {roommate.rulesAddendum && (
                    <>
                      <FaRegFilePdf className=" text-6xl" />
                      <div className="mt-2">
                        {addendumFileName.rulesAddendum.length >= 30
                          ? addendumFileName.rulesAddendum.slice(0, 30) +
                            "...pdf"
                          : addendumFileName.rulesAddendum}
                      </div>
                    </>
                  )}
                </div>
                <div className="grid justify-items-center mt-10">
                  <input
                    id="add-addendum"
                    name="rulesAddendum"
                    type="file"
                    accept="image/*, application/pdf"
                    autoFocus={true}
                    onChange={handleUpload}
                    className="block w-72 border-gray-300 border rounded-lg p-1"
                  />
                  <div className="flex justify-center text-gray-500 mt-2">
                    (File must be in .pdf format)
                  </div>
                  {fileSizeError && (
                    <div className={`text-red-500 ${error.fileSizeError}`}>
                      File is too large
                    </div>
                  )}
                  {fileTypeError && (
                    <div className={`text-red-500 ${error.fileTypeError}`}>
                      Format must be a .pdf
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
        {/* {page === 12 && (
          <>
            <div className="flex justify-center mt-10 pl-16 pr-10">
              Would you like to add any "Required State Disclosures" as an
              addendum? (Optional)
            </div>
            <label className="grid mt-5">
              <select
                id="add-disclosures-addendum"
                name="disclosuresAddendum"
                value={addendum.disclosuresAddendum}
                className="w-40 border-gray-300 border-1 rounded-md"
                autoFocus={true}
                onChange={handleAddendums}
              >
                <option value={false}>No</option>
                <option value={true}>Yes</option>
              </select>
            </label>
            {addendum.disclosuresAddendum === "true" && (
              <div>
                <div className="grid justify-items-center h-20 mt-10">
                  {roommate.disclosuresAddendum && (
                    <>
                      <FaRegFilePdf className=" text-6xl" />
                      <div className="mt-2">
                        {addendumFileName.disclosuresAddendum.length >= 30
                          ? addendumFileName.disclosuresAddendum.slice(0, 30) +
                            "...pdf"
                          : addendumFileName.disclosuresAddendum}
                      </div>
                    </>
                  )}
                </div>
                <div className="grid justify-items-center mt-10">
                  <input
                    id="add-addendum"
                    name="disclosuresAddendum"
                    type="file"
                    accept="image/*, application/pdf"
                    autoFocus={true}
                    onChange={handleUpload}
                    className="block w-72 border-gray-300 border rounded-lg p-1"
                  />
                  <div className="flex justify-center mt-2">
                    (File must be in .pdf format)
                  </div>
                  {fileSizeError && (
                    <div className={`text-red-500 ${error.fileSizeError}`}>
                      File is too large
                    </div>
                  )}
                  {fileTypeError && (
                    <div className={`text-red-500 ${error.fileTypeError}`}>
                      Format must be a .pdf
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )} */}

        {page === 12 && (
          <>
            <div className="flex justify-center mt-10 px-16">
              Would you like to add any "Other Info" as an addendum? (Optional)
            </div>
            <label className="grid mt-5">
              <select
                id="add-other-addendum"
                name="otherAddendum"
                value={addendum.otherAddendum}
                className="w-40 border-gray-300 border-1 rounded-md"
                autoFocus={true}
                onChange={handleAddendums}
              >
                <option value={false}>No</option>
                <option value={true}>Yes</option>
              </select>
            </label>
            {addendum.otherAddendum === "true" && (
              <div>
                <div className="grid justify-items-center h-20 mt-10">
                  {roommate.otherAddendum && (
                    <>
                      <FaRegFilePdf className=" text-6xl" />
                      <div className="mt-2">
                        {addendumFileName.otherAddendum.length >= 30
                          ? addendumFileName.otherAddendum.slice(0, 30) +
                            "...pdf"
                          : addendumFileName.otherAddendum}
                      </div>
                    </>
                  )}
                </div>
                <div className="grid justify-items-center mt-10">
                  <input
                    id="add-addendum"
                    name="otherAddendum"
                    type="file"
                    accept="image/*, application/pdf"
                    onChange={handleUpload}
                    className="block w-72 border-gray-300 border rounded-lg p-1"
                  />
                  <div className="flex justify-center mt-2">
                    (File must be in .pdf format)
                  </div>
                  {fileSizeError && (
                    <div className={`text-red-500 ${error.fileSizeError}`}>
                      File is too large
                    </div>
                  )}
                  {fileTypeError && (
                    <div className={`text-red-500 ${error.fileTypeError}`}>
                      Format must be a .pdf
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </form>
      {page === 13 && (
        <div className="grid justify-items-center">
          <div className="text-center mt-10 px-14">
            Would you like to add a mailing address?
          </div>
          <label className="grid mt-5">
            <select
              id="add-mailing-address"
              name="addMailingAddress"
              value={addMailingAddress}
              className="w-40 border-gray-300 border-1 rounded-md"
              autoFocus={true}
              onChange={() => {
                setAddMailingAddress(!addMailingAddress);
                resetAddress();
              }}
            >
              <option value={false}>No</option>
              <option value={true}>Yes</option>
            </select>
          </label>
          {addMailingAddress && (
            <div className="mt-10">
              <AddressForm />
            </div>
          )}
          {!addMailingAddress && (
            <div className="max-w-[500px] p-10">
              <div
                className="text-center bg-gray-100 border-gray-400 
                border-2 rounded-2xl mt-5 p-5"
              >
                An address other than the property address where tenants can
                send rent payments and/or official notices.
              </div>
            </div>
          )}
        </div>
      )}
      {/* {page === 15 && (
        <div className="grid justify-items-center">
          <div className="flex justify-center text-center mt-10 px-14">
            Accept Electronic Payments (ACH)?
          </div>
          <label className="grid mt-5">
            <select
              id="allow-electrionic-payments"
              name="allowElectronicPayments"
              value={roommate.allowElectronicPayments}
              disabled={stripeSubscription.type === "basic" ? true : false}
              autoFocus={true}
              className="w-40 border-gray-300 border-1 rounded-md"
              onChange={() =>
                setRoommate((prevState) => ({
                  ...prevState,
                  allowElectronicPayments: !roommate.allowElectronicPayments,
                }))
              }
            >
              <option value={false}>No</option>
              <option value={true}>Yes</option>
            </select>
          </label>
          {roommate.allowElectronicPayments && (
            <div className="p-10">
              <div
                className="text-center bg-gray-100 border-gray-400 
                border-2 rounded-2xl mt-5 p-5"
              >
                Payments are processed safely and securely using{" "}
                <a
                  className="text-blue-500 hover:text-blue-700 cursor-pointer"
                  href="https://stripe.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  Stripe&reg;
                </a>
                <ul className="grid justify-items-center list-disc text-green-500 mt-3 ml-2">
                  <li>Faster Payments</li>
                  <li>Automated Rent Collection</li>
                  <li>Direct Deposits to your Account</li>
                </ul>
              </div>
            </div>
          )}
          {!roommate.allowElectronicPayments && (
            <div className="pt-3 px-10">
              {stripeSubscription.type === "basic" && (
                <div className="text-center px-5">
                  <span
                    className="text-blue-500 hover:text-blue-700 cursor-pointer mx-1"
                    onClick={() => {
                      handleModal("addRoommate");
                      navigate("/account/subscription");
                    }}
                  >
                    Upgrade
                  </span>
                  to accept electronic payments.
                </div>
              )}
              <div
                className="text-center bg-gray-100 border-gray-400 
                border-2 rounded-2xl mt-12 p-5"
              >
                <div>
                  I will manually collect rent from my tenants each month.
                </div>
              </div>
            </div>
          )}
        </div>
      )} */}
      {page === pages && (
        <>
          <div className="grid justify-items-center mt-10">
            <div>Is everything correct?</div>
            <div className="text-base w-80 border-gray-300 border-2 rounded-xl mt-5 p-5">
              <div className="flex justify-center">Lease Summary:</div>
              <div className="flex justify-center capitalize mt-2">
                <div
                  className="text-blue-500 hover:text-blue-700 cursor-pointer mr-1"
                  onClick={() => setPage(1)}
                >
                  {roommate.tenantFirstName}
                </div>
                <div
                  className="text-blue-500 hover:text-blue-700 cursor-pointer whitespace-pre-wrap"
                  onClick={() => setPage(2)}
                >
                  {roommate.tenantMiddleName
                    ? `${roommate.tenantMiddleName} `
                    : ""}
                </div>
                <div
                  className="text-blue-500 hover:text-blue-700 cursor-pointer"
                  onClick={() => setPage(3)}
                >
                  {(
                    roommate.tenantFirstName +
                    roommate.tenantMiddleName +
                    roommate.tenantLastName
                  ).length > 25
                    ? `${roommate.tenantLastName.slice(0, 4)}...`
                    : roommate.tenantLastName}
                </div>
              </div>
              <div
                className="flex justify-center text-blue-500 hover:text-blue-700 cursor-pointer"
                onClick={() => setPage(4)}
              >
                {roommate.tenantEmail.length > 30
                  ? `${roommate.tenantEmail.slice(0, 30)}...`
                  : roommate.tenantEmail}
              </div>
              <div className="flex justify-between mt-5">
                <div>Term:</div>
                <div
                  className="text-blue-500 hover:text-blue-700 cursor-pointer capitalize pl-5"
                  onClick={() => setPage(7)}
                >
                  {roommate.leaseTerm}
                </div>
              </div>
              <div className="flex justify-between">
                <div>Start Date:</div>
                <div
                  className="text-blue-500 hover:text-blue-700 cursor-pointer capitalize"
                  onClick={() => setPage(8)}
                >
                  {DateTime.fromISO(roommate.leaseStart).toFormat("D")}
                </div>
              </div>
              <div className="flex justify-between">
                <div>End Date:</div>
                <div className="capitalize">
                  {roommate.leaseTerm === "1 month"
                    ? "Month-to-month"
                    : DateTime.fromISO(roommate.leaseEnd).toFormat("D")}
                </div>
              </div>
              <div className="flex justify-between mt-2 capitalize">
                <div>{roommate.payPeriod} Rent:</div>
                <div
                  className="flex justify-center text-blue-500 hover:text-blue-700 cursor-pointer"
                  onClick={() => setPage(5)}
                >
                  ${Intl.NumberFormat().format(roommate.rent)}
                </div>
              </div>
              <div className="flex justify-between">
                <div>Security Deposit:</div>
                <div
                  className="flex justify-center text-blue-500 hover:text-blue-700 cursor-pointer"
                  onClick={() => setPage(5)}
                >
                  ${Intl.NumberFormat().format(roommate.securityDep)}
                </div>
              </div>
              {roommate.petsAllowed && (
                <div className="flex justify-between">
                  <div>Pet Deposit:</div>
                  <div
                    className="flex justify-center text-blue-500 hover:text-blue-700 cursor-pointer"
                    onClick={() => setPage(10)}
                  >
                    ${Intl.NumberFormat().format(roommate.petDeposit)}
                  </div>
                </div>
              )}
              <div className="flex justify-between mt-2">
                {prorate.amount !== 0 && (
                  <>
                    <div>
                      First{" "}
                      {roommate.payPeriod === "weekly" ? "Week's" : "Month's"}{" "}
                      Pro-rate:
                    </div>
                    <div
                      className="flex justify-center text-blue-500 hover:text-blue-700 cursor-pointer"
                      onClick={() => setPage(8)}
                    >
                      {Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "USD",
                        maximumFractionDigits: 2,
                      }).format(prorate.amount)}
                    </div>
                  </>
                )}
              </div>
              <div className="flex justify-between">
                <div>Contributes to Bills:</div>
                <div
                  className="flex justify-center text-blue-500 hover:text-blue-700 cursor-pointer"
                  onClick={() => setPage(6)}
                >
                  {roommate.splitsBills ? "Yes" : "No"}
                </div>
              </div>
              <div className="flex justify-between">
                <div>Collect Late Fees:</div>
                <div
                  className="flex justify-center text-blue-500 hover:text-blue-700 cursor-pointer"
                  onClick={() => setPage(9)}
                >
                  {roommate.paysLateFee ? "Yes" : "No"}
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center items-start px-14 mt-5">
            <input
              id="tou"
              name="tou"
              type="checkbox"
              checked={terms}
              onChange={() => setTerms(!terms)}
              className="mt-1 mr-2"
            />
            <div
              className={`${error.terms} text-justify`}
              // onClick={() => setTerms(!terms)}
            >
              By sending this invite, I agree to Snappily's{" "}
              <span className="text-blue-500 hover:text-blue-700 underline cursor-pointer">
                terms of use
              </span>
              .
            </div>
          </div>
        </>
      )}

      <div
        className={`flex justify-center w-full mt-10 ${
          page === pages ? "inline-block mb-40" : "fixed top-[525px]"
        }`}
      >
        <Button
          background="bg-slate-400"
          text="text-white"
          opacity="bg-opacity-100"
          title={page === 1 ? "Cancel" : "Back"}
          height="h-10"
          width="w-24"
          borderColor="border-none"
          borderHover="hover:border-snappily-orange"
          customFunc={page === 1 ? () => handleModal("addRoommate") : prevPage}
        />
        <Button
          background="bg-snappily-orange"
          text="text-white"
          opacity="bg-opacity-100"
          title={page === pages ? (lease ? "Update" : "Invite") : "Next"}
          height="h-10"
          width="w-24"
          borderColor="border-none"
          borderHover="hover:border-snappily-orange"
          isDisabled={isLoading}
          customFunc={
            page === pages
              ? lease
                ? handleLeaseUpdate
                : handleAddRoommate
              : nextPage
          }
        />
      </div>
    </div>
  );
};

export default AddRoommate;
